import LegalLayout from '@/components/feature/LegalLayout';
import { useTranslation } from 'react-i18next';

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('privacy.title', 'Privacy Policy')} lastUpdated="May 20, 2026">
      <div className="space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s1', '1. Introduction')}</h2>
          <p className="leading-relaxed">
            ZhiSu Inc. (&quot;ZhiSu&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our platform, website, mobile applications, and related services (collectively, the &quot;Services&quot;).
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s2', '2. Information We Collect')}</h2>
          <p className="leading-relaxed mb-3">
            We collect several types of information from and about users of our Services:
          </p>
          <h3 className="font-medium mb-2">2.1 Information You Provide to Us</h3>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed mb-4">
            <li><strong>Account Information:</strong> Wallet addresses, email addresses (optional), and any information you provide when contacting support.</li>
            <li><strong>Verification Data:</strong> Information collected during KYC/AML verification processes, including government-issued identification documents.</li>
            <li><strong>Communication Data:</strong> Records of your correspondence with us, including emails, chat messages, and support tickets.</li>
          </ul>
          <h3 className="font-medium mb-2">2.2 Information Collected Automatically</h3>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, and language preferences.</li>
            <li><strong>Usage Data:</strong> Trading activity, API requests, pages visited, features used, and time spent on the platform.</li>
            <li><strong>Log Data:</strong> Server logs, error reports, and performance metrics for debugging and security purposes.</li>
            <li><strong>Cookies and Similar Technologies:</strong> See our Cookie Policy for detailed information.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s3', '3. How We Use Your Information')}</h2>
          <p className="leading-relaxed mb-3">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Service Provision:</strong> To operate, maintain, and improve the Services, including executing trades, processing transactions, and providing customer support.</li>
            <li><strong>Security and Fraud Prevention:</strong> To detect, prevent, and investigate fraud, unauthorized access, money laundering, and other illegal activities.</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal obligations, including tax reporting and regulatory requirements.</li>
            <li><strong>Platform Improvement:</strong> To analyze usage patterns, troubleshoot issues, and develop new features and services.</li>
            <li><strong>Communication:</strong> To send you service-related notifications, updates, security alerts, and marketing communications (where permitted).</li>
            <li><strong>Personalization:</strong> To customize your experience and provide content and features relevant to your interests.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s4', '4. Legal Basis for Processing')}</h2>
          <p className="leading-relaxed mb-3">
            We process your personal data based on the following legal grounds:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Contractual Necessity:</strong> Processing necessary to perform our contract with you (providing the Services).</li>
            <li><strong>Legal Obligation:</strong> Processing required to comply with laws and regulations (AML/KYC, tax reporting).</li>
            <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate interests, such as platform security, fraud prevention, and service improvement.</li>
            <li><strong>Consent:</strong> Processing based on your explicit consent, particularly for marketing communications.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s5', '5. Information Sharing and Disclosure')}</h2>
          <p className="leading-relaxed mb-3">
            We do not sell your personal information. We may share your information in the following circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf, such as cloud hosting, analytics, and customer support, under strict confidentiality agreements.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority, including regulatory investigations and law enforcement requests.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, subject to confidentiality obligations.</li>
            <li><strong>Protection of Rights:</strong> To enforce our Terms of Service, protect our rights, property, or safety, or the rights, property, or safety of others.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s6', '6. Data Security')}</h2>
          <p className="leading-relaxed">
            We implement industry-standard security measures to protect your information, including encryption in transit (TLS 1.3), encryption at rest, access controls, and regular security audits. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s7', '7. Data Retention')}</h2>
          <p className="leading-relaxed">
            We retain your personal data for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Trading records and KYC documentation are typically retained for 7 years to comply with financial regulations. Account information is retained for the duration of your account plus a reasonable period thereafter for legal and operational purposes.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s8', '8. Your Rights')}</h2>
          <p className="leading-relaxed mb-3">
            Depending on your jurisdiction, you may have the following rights regarding your personal data:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Access:</strong> The right to request copies of your personal data.</li>
            <li><strong>Rectification:</strong> The right to request correction of inaccurate or incomplete data.</li>
            <li><strong>Erasure:</strong> The right to request deletion of your personal data, subject to legal retention requirements.</li>
            <li><strong>Restriction:</strong> The right to request limitation of processing in certain circumstances.</li>
            <li><strong>Portability:</strong> The right to receive your data in a structured, machine-readable format.</li>
            <li><strong>Objection:</strong> The right to object to processing based on legitimate interests or for direct marketing.</li>
            <li><strong>Withdraw Consent:</strong> The right to withdraw consent where processing is based on consent.</li>
          </ul>
          <p className="leading-relaxed mt-3">
            To exercise these rights, please contact us at privacy@zhisu.trade. We will respond within 30 days of receiving your request.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s9', '9. International Data Transfers')}</h2>
          <p className="leading-relaxed">
            Your information may be transferred to and processed in countries other than the country in which you reside. These countries may have data protection laws that are different from the laws of your country. We ensure appropriate safeguards are in place for such transfers, including standard contractual clauses approved by relevant regulatory authorities.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s10', '10. Children\'s Privacy')}</h2>
          <p className="leading-relaxed">
            The Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child without verification of parental consent, we will take steps to delete that information.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s11', '11. Changes to This Policy')}</h2>
          <p className="leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the &quot;Last Updated&quot; date. Your continued use of the Services after such changes constitutes your acceptance of the revised policy.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('privacy.s12', '12. Contact Us')}</h2>
          <p className="leading-relaxed">
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at privacy@zhisu.trade or through our Support Center. You may also contact our Data Protection Officer at dpo@zhisu.trade.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}